import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  fetchProcurementDisplacementCells,
  fetchProcurementBenchmarkPoints,
} from '@/lib/api/procurementServer';
import { normalizeDisplacementCells } from '@/lib/procurementDisplacementAdapters';

/**
 * Server data for displacement + benchmark tabs (Path A).
 * Replaces client closed-deals bulk load for these views.
 */
export function useProcurementServerTabs({
  tenantId,
  activeTab,
  categories = [],
  countries = [],
  institutionTypes = [],
  suppliers = [],
  search = '',
  /** Skip Path A — parent builds cells/points from tenant-owned closed_deals. */
  ownClosedDealsOnly = false,
}) {
  // [2026-10-11] The Displacement tab now renders CategoryDisplacement, which
  // reads /category-intel and never touches these cells — nothing in the app
  // consumes displacementCells / loadingDisplacement / displacementError any
  // more. Leaving the fetch enabled kept firing procurement_displacement_cells
  // on every visit to the tab, a heavy aggregate that was timing out (57014)
  // alongside /contract-table and /query. The hook's return shape is unchanged
  // so callers destructuring it keep working.
  const displacementEnabled = false;
  const benchmarkEnabled =
    !ownClosedDealsOnly && activeTab === 'benchmark' && !!tenantId && categories.length > 0;

  const [displacementRaw, setDisplacementRaw] = useState(null);
  const [benchmarkRaw, setBenchmarkRaw] = useState(null);
  const [loadingDisplacement, setLoadingDisplacement] = useState(false);
  const [loadingBenchmark, setLoadingBenchmark] = useState(false);
  const [displacementError, setDisplacementError] = useState(null);
  const [benchmarkError, setBenchmarkError] = useState(null);

  const filterKey = useMemo(
    () =>
      JSON.stringify({
        categories,
        countries,
        institutionTypes,
        suppliers,
        search: search?.trim() || '',
        activeTab,
      }),
    [categories, countries, institutionTypes, suppliers, search, activeTab],
  );

  const baseFilters = useMemo(
    () => ({
      categories,
      countries,
      institutionTypes,
      suppliers,
      search: search?.trim() || undefined,
    }),
    [categories, countries, institutionTypes, suppliers, search],
  );

  const loadDisplacement = useCallback(async () => {
    if (!displacementEnabled) return;
    setLoadingDisplacement(true);
    setDisplacementError(null);
    try {
      const data = await fetchProcurementDisplacementCells(tenantId, baseFilters);
      setDisplacementRaw(data);
    } catch (err) {
      setDisplacementError(err?.message || 'Failed to load displacement data');
      setDisplacementRaw(null);
    } finally {
      setLoadingDisplacement(false);
    }
  }, [displacementEnabled, tenantId, baseFilters]);

  const loadBenchmark = useCallback(async () => {
    if (!benchmarkEnabled) return;
    setLoadingBenchmark(true);
    setBenchmarkError(null);
    try {
      const data = await fetchProcurementBenchmarkPoints(tenantId, baseFilters);
      setBenchmarkRaw(data);
    } catch (err) {
      setBenchmarkError(err?.message || 'Failed to load benchmark data');
      setBenchmarkRaw(null);
    } finally {
      setLoadingBenchmark(false);
    }
  }, [benchmarkEnabled, tenantId, baseFilters]);

  useEffect(() => {
    if (displacementEnabled) loadDisplacement();
    else {
      setDisplacementRaw(null);
      setDisplacementError(null);
    }
  }, [displacementEnabled, filterKey, loadDisplacement]);

  useEffect(() => {
    if (benchmarkEnabled) loadBenchmark();
    else {
      setBenchmarkRaw(null);
      setBenchmarkError(null);
    }
  }, [benchmarkEnabled, filterKey, loadBenchmark]);

  const displacementCells = useMemo(
    () => normalizeDisplacementCells(displacementRaw?.cells),
    [displacementRaw],
  );

  const benchmarkPoints = useMemo(() => benchmarkRaw?.points || [], [benchmarkRaw]);

  return {
    displacementCells,
    benchmarkPoints,
    loadingDisplacement,
    loadingBenchmark,
    displacementError,
    benchmarkError,
  };
}
